import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { esES } from '@mui/material/locale';
import CssBaseline from '@mui/material/CssBaseline';
import { Container, Box, Alert } from '@mui/material';
import Header from './components/Header';
import InvoiceList from './components/InvoiceList';
import InvoiceAccountingExamples from './components/InvoiceAccountingExamples';
import LoginForm from './components/LoginForm';
import InvoiceLocalServiceConfigModal from './components/InvoiceLocalServiceConfigModal';
import GeneralSettingsModal from './components/GeneralSettingsModal';
import supabaseClient from '../config';
import createInvoiceStore from './stores/invoiceStore.mjs';
import createCompanyStore from './stores/companyStore.mjs';
import createUtilityBillStore from './stores/utilityBillStore.mjs';
import createAuthStore from './stores/authStore.mjs';
import createFiscalStore from './stores/fiscalStore.mjs';
import createThirdPartyStore from './stores/thirdPartyStore.mjs';
import createErpListsStore from './stores/erpListsStore.mjs';
import FiscalConfigView from './components/FiscalConfigView';
import ThirdPartyConfig from './components/ThirdPartyConfig';
import DianExtractorView from './components/DianExtractorView';
import SubUsersManagement from './components/SubUsersManagement';
import { isDemoAccount } from './lib/demoAccount.mjs';

const theme = createTheme({
  palette: {
    primary: { main: '#4361ee', light: '#4895ef', dark: '#3f37c9' },
    secondary: { main: '#f72585' },
    success: { main: '#4cc9f0' },
    warning: { main: '#f8961e' },
    error: { main: '#f94144' },
    background: { default: '#f5f7fa' },
  },
  typography: {
    fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: { root: { textTransform: 'none' } },
    },
  },
});

const demoTheme = createTheme(theme, esES);

const App = () => {
  const [currentView, setCurrentView] = useState('invoices');
  const [isConnected, setIsConnected] = useState(false);
  const [stores, setStores] = useState(null);
  const [authStore, setAuthStore] = useState(null);
  const [user, setUser] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [localServiceConfigOpen, setLocalServiceConfigOpen] = useState(false);
  const [generalSettingsOpen, setGeneralSettingsOpen] = useState(false);

  useEffect(() => {
    // Initialize auth store first
    const authStoreInstance = createAuthStore(supabaseClient);
    setAuthStore(authStoreInstance);

    // Check for existing session
    const checkAuth = async () => {
      const isValid = await authStoreInstance.getState().validateSession();
      if (isValid) {
        const currentUser = authStoreInstance.getState().user;
        setUser(currentUser);
        initializeStores(currentUser);
        authStoreInstance.getState().fetchDocTypeWarehouses();
      }
      setIsAuthenticating(false);
    };

    checkAuth();
  }, []);

  const initializeStores = (authenticatedUser) => {
    if (!authenticatedUser) return;

    // Initialize stores with user authentication
    const getUserId = () => authenticatedUser.id;
    const invoiceStore = createInvoiceStore(supabaseClient, getUserId);
    const companyStore = createCompanyStore(supabaseClient, getUserId);
    const utilityBillStore = createUtilityBillStore(supabaseClient, getUserId);
    const fiscalStore = createFiscalStore(supabaseClient, getUserId);
    const thirdPartyStore = createThirdPartyStore(supabaseClient, getUserId);
    const erpListsStore = createErpListsStore(supabaseClient, getUserId);

    setStores({
      invoiceStore,
      companyStore,
      utilityBillStore,
      fiscalStore,
      thirdPartyStore,
      erpListsStore,
    });

    // Fetch initial data after stores are created
    invoiceStore.getState().fetchMonthlySummary();
    companyStore.getState().fetchCompanies();
    utilityBillStore.getState().fetchUtilityBills();
    fiscalStore.getState().fetchFiscalData();
    thirdPartyStore.getState().fetchThirdParties();
    erpListsStore.getState().fetchLists();

    // Setup realtime subscriptions
    invoiceStore.getState().setupRealtimeSubscription();

    // Set up connection monitoring
    const checkConnection = async () => {
      try {
        const { error } = await supabaseClient.from('invoices').select('count', { count: 'exact', head: true });
        const connected = !error;
        setIsConnected(connected);
        
        // Update connection status in all stores
        invoiceStore.getState().setConnectionStatus(connected);
        companyStore.getState().setConnectionStatus(connected);
        utilityBillStore.getState().setConnectionStatus(connected);
        fiscalStore.getState().setConnectionStatus(connected);
        thirdPartyStore.getState().setConnectionStatus(connected);
      } catch (error) {
        console.error('Connection check failed:', error);
        setIsConnected(false);
        invoiceStore.getState().setConnectionStatus(false);
        companyStore.getState().setConnectionStatus(false);
        utilityBillStore.getState().setConnectionStatus(false);
        fiscalStore.getState().setConnectionStatus(false);
        thirdPartyStore.getState().setConnectionStatus(false);
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 30000);

    return () => clearInterval(interval);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const handleLogin = async (email, password) => {
    if (!authStore) return { success: false, error: 'Auth store not initialized' };
    
    const result = await authStore.getState().login(email, password);
    if (result.success) {
      const loggedInUser = authStore.getState().user;
      setUser(loggedInUser);
      initializeStores(loggedInUser);
      authStore.getState().fetchDocTypeWarehouses();
    }
    return result;
  };

  const handleLogout = async () => {
    if (!authStore) return;
    
    await authStore.getState().logout();
    setUser(null);
    setStores(null);
    setCurrentView('invoices');
  };

  const handleOpenLocalServiceConfig = () => {
    setLocalServiceConfigOpen(true);
  };

  const handleCloseLocalServiceConfig = () => {
    setLocalServiceConfigOpen(false);
  };

  const handleOpenGeneralSettings = () => {
    setGeneralSettingsOpen(true);
  };

  const handleCloseGeneralSettings = () => {
    setGeneralSettingsOpen(false);
  };

  if (isAuthenticating) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            Loading...
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="sm">
          <Box sx={{ mt: 8 }}>
            <LoginForm onLogin={handleLogin} />
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (!stores) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            Initializing stores...
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={isDemoAccount(user) ? demoTheme : theme}>
      <CssBaseline />
      <div lang={isDemoAccount(user) ? 'es-CO' : undefined}>
        <Header
          isConnected={isConnected}
          currentView={currentView}
          onViewChange={handleViewChange}
          user={user}
          onLogout={handleLogout}
          onOpenLocalServiceConfig={handleOpenLocalServiceConfig}
          onOpenGeneralSettings={handleOpenGeneralSettings}
        />
        <GeneralSettingsModal
          open={generalSettingsOpen}
          onClose={handleCloseGeneralSettings}
          authState={authStore.getState()}
          authStore={authStore}
          supabase={supabaseClient}
        />
        <InvoiceLocalServiceConfigModal
          open={localServiceConfigOpen}
          onClose={handleCloseLocalServiceConfig}
          user={user}
          erpListsStore={stores.erpListsStore}
        />
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          {isDemoAccount(user) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Cuenta de demostración · Datos ficticios · Sin conexión a un ERP real.
            </Alert>
          )}
          {currentView === 'invoices' ? (
            <InvoiceList invoiceStore={stores.invoiceStore} erpListsStore={stores.erpListsStore} authStore={authStore} />
          ) : currentView === 'accounting_examples' ? (
            <InvoiceAccountingExamples />
          ) : currentView === 'fiscal_config' ? (
            <FiscalConfigView fiscalStore={stores.fiscalStore} />
          ) : currentView === 'third_parties' ? (
            <ThirdPartyConfig thirdPartyStore={stores.thirdPartyStore} />
          ) : currentView === 'dian_extractor' ? (
            <DianExtractorView />
          ) : currentView === 'sub_users' ? (
            <SubUsersManagement user={user} />
          ) : (
            <InvoiceList invoiceStore={stores.invoiceStore} erpListsStore={stores.erpListsStore} authStore={authStore} />
          )}
        </Container>
      </div>
    </ThemeProvider>
  );
}

export default App;
